import ExcelJS from 'exceljs';

import { computeBreakeven, statusLine } from './breakeven';
import { calculate } from './calc';
import { computeCashflow } from './cashflow';
import { currencyByCode } from './currency';
import { applyScenario, SCENARIO_LABELS } from './scenarios';
import {
  costCategoriesFor,
  type CostCategory,
  type EventData,
  type EventMeta,
} from './types';

const TRAILING_SYMBOLS = new Set(['zł', 'kr', 'Kč']);

function moneyFmt(currencyCode: string): string {
  const sym = currencyByCode(currencyCode).symbol;
  if (TRAILING_SYMBOLS.has(sym)) return `#,##0.00 "${sym}"`;
  return `"${sym}"#,##0.00`;
}

export async function exportEventToExcel(
  meta: EventMeta,
  data: EventData,
): Promise<void> {
  const isMeetup = meta.event_type === 'meetup';
  const effective = isMeetup ? data : applyScenario(data);
  const totals = calculate(effective);
  const gross = data.vatMode === 'gross' ? 1 + data.vatRate : 1;
  const fmt = moneyFmt(meta.currency);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'estimate.events';
  wb.created = new Date();

  buildSummary(wb, meta, data, totals, gross, fmt, isMeetup, effective);
  buildIncomes(wb, data, gross, fmt);
  buildCosts(wb, meta, data, gross, fmt);
  if (!isMeetup && meta.start_date) {
    buildCashflow(wb, effective, meta.start_date, gross, fmt);
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = makeFileName(meta);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildSummary(
  wb: ExcelJS.Workbook,
  meta: EventMeta,
  data: EventData,
  totals: ReturnType<typeof calculate>,
  gross: number,
  fmt: string,
  isMeetup: boolean,
  effective: EventData,
) {
  const ws = wb.addWorksheet('Summary');
  ws.columns = [{ width: 30 }, { width: 22 }, { width: 22 }];

  const head = (label: string) => {
    const r = ws.addRow([label]);
    r.font = { bold: true };
    return r;
  };
  const pair = (label: string, value: unknown) => ws.addRow([label, value]);
  const money = (label: string, vmin: number, vmax: number) => {
    const row = ws.addRow([label, vmin * gross, vmax * gross]);
    row.getCell(2).numFmt = fmt;
    row.getCell(3).numFmt = fmt;
    return row;
  };

  head('Event');
  pair('Name', meta.name || '(untitled)');
  pair('Type', isMeetup ? 'Meetup' : 'Conference');
  pair('Country', meta.country_code);
  pair('City', meta.city);
  pair('Start date', meta.start_date ?? '');
  pair('End date', meta.end_date ?? '');
  if (!isMeetup) {
    pair('Conference days', meta.conference_days);
    pair('Workshop days', meta.workshop_days);
  }
  pair('Currency', meta.currency);
  pair(
    'VAT mode',
    data.vatMode === 'gross'
      ? `Gross (+${(data.vatRate * 100).toFixed(1)}% VAT)`
      : 'Net',
  );
  if (!isMeetup) pair('Scenario', SCENARIO_LABELS[data.scenario.active]);

  ws.addRow([]);
  const totalsHeader = ws.addRow(['Totals', 'Min', 'Max']);
  totalsHeader.font = { bold: true };
  money('Revenue', totals.revenue.min, totals.revenue.max);
  money('  Tickets', totals.ticketRevenue.min, totals.ticketRevenue.max);
  money('  Sponsors', totals.sponsorRevenue.min, totals.sponsorRevenue.max);
  money('  Add-ons', totals.addonRevenue, totals.addonRevenue);
  money('Costs', totals.costs, totals.costs);
  money('Net (revenue − costs)', totals.net.min, totals.net.max);

  ws.addRow([]);
  head('Counts');
  pair('Tickets sold', totals.ticketCount);
  pair('Sponsors', totals.sponsorCount);
  pair('Add-ons enabled', totals.addonCount);

  if (!isMeetup) {
    const be = computeBreakeven(effective);
    const status = statusLine(be, meta.currency);
    ws.addRow([]);
    head('Break-even');
    if (status) pair('Status', status.text);
    pair(
      'At MIN prices',
      be.min.profitable ? 'Profitable' : 'Below break-even',
    );
    if (!be.min.profitable) {
      const r = ws.addRow(['  Gap', be.min.gap * gross]);
      r.getCell(2).numFmt = fmt;
      if (be.min.tickets.computable) pair('  Tickets needed', be.min.tickets.needed);
      if (be.min.sponsors.computable) pair('  Sponsors needed', be.min.sponsors.needed);
    }
    pair(
      'At MAX prices',
      be.max.profitable ? 'Profitable' : 'Below break-even',
    );
    if (!be.max.profitable) {
      const r = ws.addRow(['  Gap', be.max.gap * gross]);
      r.getCell(2).numFmt = fmt;
    }
  }
}

function buildIncomes(
  wb: ExcelJS.Workbook,
  data: EventData,
  gross: number,
  fmt: string,
) {
  const ws = wb.addWorksheet('Incomes');
  ws.columns = [
    { header: 'Section', key: 'section', width: 14 },
    { header: 'Name', key: 'name', width: 30 },
    { header: 'Qty / Multiplier', key: 'qty', width: 18 },
    { header: 'Price (min)', key: 'pmin', width: 16 },
    { header: 'Price (max)', key: 'pmax', width: 16 },
    { header: 'Subtotal (min)', key: 'smin', width: 18 },
    { header: 'Subtotal (max)', key: 'smax', width: 18 },
  ];
  ws.getRow(1).font = { bold: true };

  const moneyCells = [4, 5, 6, 7];
  const applyFmt = (row: ExcelJS.Row) => {
    for (const i of moneyCells) row.getCell(i).numFmt = fmt;
  };

  for (const t of data.tickets) {
    const row = ws.addRow({
      section: 'Ticket',
      name: t.name,
      qty: t.qty,
      pmin: t.priceMin * gross,
      pmax: t.priceMax * gross,
      smin: t.qty * t.priceMin * gross,
      smax: t.qty * t.priceMax * gross,
    });
    applyFmt(row);
  }
  for (const sp of data.sponsors) {
    const row = ws.addRow({
      section: 'Sponsor',
      name: sp.name,
      qty: sp.qty,
      pmin: sp.priceMin * gross,
      pmax: sp.priceMax * gross,
      smin: sp.qty * sp.priceMin * gross,
      smax: sp.qty * sp.priceMax * gross,
    });
    applyFmt(row);
  }
  for (const a of data.addons) {
    const mult = a.multiplier && a.multiplier > 0 ? a.multiplier : 1;
    const total = a.enabled ? a.price * mult * gross : 0;
    const row = ws.addRow({
      section: 'Add-on',
      name: `${a.name}${a.enabled ? '' : ' (disabled)'}`,
      qty: mult,
      pmin: a.price * gross,
      pmax: a.price * gross,
      smin: total,
      smax: total,
    });
    applyFmt(row);
  }
}

function buildCosts(
  wb: ExcelJS.Workbook,
  meta: EventMeta,
  data: EventData,
  gross: number,
  fmt: string,
) {
  const ws = wb.addWorksheet('Costs');
  ws.columns = [
    { header: 'Category', key: 'cat', width: 18 },
    { header: 'Item', key: 'name', width: 34 },
    { header: 'Amount', key: 'amount', width: 16 },
  ];
  ws.getRow(1).font = { bold: true };

  let grand = 0;
  for (const c of costCategoriesFor(meta.event_type)) {
    const items = data.costs[c.key as CostCategory] ?? [];
    if (items.length === 0) continue;
    for (const item of items) {
      const row = ws.addRow({
        cat: c.label,
        name: item.name || '(no name)',
        amount: item.amount * gross,
      });
      row.getCell(3).numFmt = fmt;
    }
    const sub = items.reduce((acc, i) => acc + i.amount, 0) * gross;
    grand += sub;
    const subRow = ws.addRow({
      cat: c.label,
      name: '— subtotal',
      amount: sub,
    });
    subRow.font = { italic: true };
    subRow.getCell(3).numFmt = fmt;
  }
  const grandRow = ws.addRow({ cat: 'TOTAL', name: '', amount: grand });
  grandRow.font = { bold: true };
  grandRow.getCell(3).numFmt = fmt;
}

function buildCashflow(
  wb: ExcelJS.Workbook,
  effective: EventData,
  startDate: string,
  gross: number,
  fmt: string,
) {
  const cf = computeCashflow(effective, startDate);
  const ws = wb.addWorksheet('Cashflow');
  ws.columns = [
    { header: 'Week starting', key: 'start', width: 14 },
    { header: 'Week ending', key: 'end', width: 14 },
    { header: 'Days to event', key: 'd', width: 14 },
    { header: 'Inflow', key: 'in', width: 14 },
    { header: 'Outflow', key: 'out', width: 14 },
    { header: 'Net (week)', key: 'net', width: 14 },
    { header: 'Cumulative', key: 'cum', width: 14 },
    { header: 'Movements', key: 'mv', width: 70 },
  ];
  ws.getRow(1).font = { bold: true };

  for (const w of cf.weeks) {
    const row = ws.addRow({
      start: w.weekStart,
      end: w.weekEnd,
      d: w.daysToEvent,
      in: w.inflow * gross,
      out: w.outflow * gross,
      net: (w.inflow - w.outflow) * gross,
      cum: w.cumulative * gross,
      mv: w.movements
        .map((m) => {
          const v = Math.round(m.amount * gross);
          return `${m.description}: ${v >= 0 ? '+' : ''}${v}`;
        })
        .join(' | '),
    });
    for (const i of [4, 5, 6, 7]) row.getCell(i).numFmt = fmt;
  }

  ws.addRow([]);
  const inRow = ws.addRow(['Total inflow', '', '', cf.totalIn * gross]);
  inRow.font = { bold: true };
  inRow.getCell(4).numFmt = fmt;
  const outRow = ws.addRow(['Total outflow', '', '', '', cf.totalOut * gross]);
  outRow.font = { bold: true };
  outRow.getCell(5).numFmt = fmt;
  const netRow = ws.addRow(['Final net', '', '', '', '', '', cf.finalNet * gross]);
  netRow.font = { bold: true };
  netRow.getCell(7).numFmt = fmt;
  if (cf.trough) {
    const tr = ws.addRow([
      `Trough on ${cf.trough.date}`,
      '',
      '',
      '',
      '',
      '',
      cf.trough.amount * gross,
    ]);
    tr.getCell(7).numFmt = fmt;
  }
}

function makeFileName(meta: EventMeta): string {
  const safe = (meta.name || 'event')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const date = meta.start_date ? `_${meta.start_date}` : '';
  return `${safe || 'event'}${date}.xlsx`;
}
